import React, {useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {common as stringsCommon, forgotEmail as strings} from "strings";

import AlertModal from "components/AlertModal";
import Button from "components/Button";
import {DateString} from "components/DateInput";
import StyledDateInput from "components/StyledDateInput";
import TextInput from "components/TextInput";
import UnauthLayout from "components/UnauthLayout";

import {useForgotEmail} from "liberty-core/api/react-query/user";
import {formatPrescriptionNumber} from "liberty-core/sharedFunctions";

import {useCurrentPharmacy} from "lib/useCurrentPharmacy";

import styles from "./ForgotEmailPage.module.css";

const errorFieldNames = {
  firstName: "FirstName",
  lastName: "LastName",
  dateOfBirth: "DateOfBirth",
  previousScriptNumber: "PreviousScriptNumber",
};

export default function ForgotEmailPage() {
  const [pharmacy] = useCurrentPharmacy();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState<DateString>("");
  const [previousScriptNumber, setPreviousScriptNumber] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{[field: string]: string[]}>(
    {},
  );

  const {mutateAsync: submitForgotEmailRequest, isLoading} = useForgotEmail();

  const onSubmit = (e: React.FormEvent) => {
    if (e) e.preventDefault();

    if (
      isLoading ||
      !firstName ||
      !lastName ||
      !dateOfBirth ||
      !previousScriptNumber
    ) {
      return;
    }

    submitForgotEmailRequest({
      firstName,
      lastName,
      dateOfBirth,
      previousScriptNumber: parseInt(previousScriptNumber),
      pharmacyId: pharmacy?.id ?? 0,
    })
      .then((data) => {
        navigate(`/login?${searchParams.toString()}`, {
          state: {recoveredEmail: data.email},
        });
      })
      .catch((err) => {
        if (err?.response?.status === 400 && err?.response?.data?.errors) {
          setFieldErrors(err.response.data.errors);
        }
        setErrorMessage(
          err?.response?.data?.generic?.join("\n") ??
            stringsCommon.somethingWentWrong,
        );
      });
  };

  return (
    <UnauthLayout
      rightLink={{
        label: stringsCommon.login,
        to: `/login?${searchParams.toString()}`,
      }}
    >
      <form onSubmit={onSubmit}>
        <div className={styles.title}>{strings.title}</div>
        <div className={styles.description}>{strings.description}</div>
        <TextInput
          className={styles.input}
          label={stringsCommon.firstName}
          type="text"
          autoComplete="given-name"
          value={firstName}
          onChangeText={(text) => {
            setFirstName(text);
            setFieldErrors((prev) => ({
              ...prev,
              [errorFieldNames.firstName]: [],
            }));
          }}
          errorText={fieldErrors[errorFieldNames.firstName]?.join("\n")}
        />
        <TextInput
          className={styles.input}
          label={stringsCommon.lastName}
          type="text"
          autoComplete="family-name"
          value={lastName}
          onChangeText={(text) => {
            setLastName(text);
            setFieldErrors((prev) => ({
              ...prev,
              [errorFieldNames.lastName]: [],
            }));
          }}
          errorText={fieldErrors[errorFieldNames.lastName]?.join("\n")}
        />
        <StyledDateInput
          className={styles.input}
          label={stringsCommon.dateOfBirth}
          autoComplete="bday"
          value={dateOfBirth}
          onChangeDate={(date) => {
            setDateOfBirth(date);
            setFieldErrors((prev) => ({
              ...prev,
              [errorFieldNames.dateOfBirth]: [],
            }));
          }}
          errorText={fieldErrors[errorFieldNames.dateOfBirth]?.join("\n")}
        />
        <TextInput
          className={styles.input}
          label={stringsCommon.previousScriptNumber}
          type="text"
          value={previousScriptNumber}
          onChangeText={(text) => {
            setPreviousScriptNumber(formatPrescriptionNumber(text));
            setFieldErrors((prev) => ({
              ...prev,
              [errorFieldNames.previousScriptNumber]: [],
            }));
          }}
          maxLength={9}
          errorText={fieldErrors[errorFieldNames.previousScriptNumber]?.join(
            "\n",
          )}
        />
        <Button
          className={styles.button}
          variant="filled"
          type="submit"
          onClick={onSubmit}
          loading={isLoading}
          disabled={
            !firstName || !lastName || !dateOfBirth || !previousScriptNumber
          }
        >
          {stringsCommon.submit}
        </Button>
      </form>
      <AlertModal
        title={stringsCommon.whoops}
        message={errorMessage}
        visible={!!errorMessage}
        onClickConfirm={() => setErrorMessage("")}
      />
    </UnauthLayout>
  );
}
